import { useRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { ReviewCounts } from "../../model/caseReview";
import { aiReviewed, hasFollowupWork, requiredLeft } from "../../utils/reviewCounts";
import { REVIEW_COUNT_NONE } from "../../constants/caseReviewLabels";
import { ReviewCountDetails, reviewCountFacts } from "./ReviewCountDetails";

export type ReviewCounterVariant = "compact" | "regular" | "ring";

interface Props {
  /** The scope's name: the card's title and the start of the screen-reader summary. */
  label: string;
  /** Absent for a scope that has no counts at all (a panel): nothing is rendered. */
  counts: ReviewCounts | undefined;
  variant?: ReviewCounterVariant;
  /** Where the card opens relative to the count. */
  side?: "right" | "bottom";
}

/** How long the pointer may be off the count and the card before the card closes. */
const CLOSE_DELAY_MS = 150;
const RING_RADIUS = 7.5;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * The fraction a count shows: AI reviewed of AI total. A follow-up-only scope (the Follow-up
 * changes entry) shows its answers of its questions: each record to resolve and each proposal.
 */
function fractionOf(counts: ReviewCounts): { done: number; total: number } | undefined {
  if (counts.ai.total > 0) return { done: aiReviewed(counts), total: counts.ai.total };
  if (counts.required.total === 0 && hasFollowupWork(counts.followup)) {
    const { decided, total, records } = counts.followup;
    return { done: decided + records.resolved, total: total + records.total };
  }
  return undefined;
}

/**
 * A scope's count: a red pill with the required fields still empty (only when there are any) and
 * the AI values reviewed of the AI values extracted, or a ring of the same ratio.
 *
 * <p>Never interactive itself. It sits inside rail rows, tabs and chips that are already buttons,
 * so it renders a span and anchors its details to it; the button keeps its click and focus. The
 * numbers are also in the button's text for a screen reader, as a hidden sentence after the name
 * the button already shows.
 */
export function ReviewCounter({ label, counts, variant = "compact", side = "bottom" }: Props) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  if (!counts) return null;

  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS); };
  const left = requiredLeft(counts);
  const fraction = fractionOf(counts);
  const complete = !!fraction && fraction.done === fraction.total;
  const facts = reviewCountFacts(counts);

  return <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
    <PopoverPrimitive.Anchor asChild>
      <span className="cr-count" data-variant={variant} onPointerEnter={show} onPointerLeave={hide}>
        {left > 0 && <span className="cr-count-required" aria-hidden="true">{left}</span>}
        {variant === "ring" && <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="cr-count-ring">
          <circle cx="9" cy="9" r={RING_RADIUS} className="cr-count-ring-track" />
          {fraction && <circle cx="9" cy="9" r={RING_RADIUS} className="cr-count-ring-fill" data-complete={complete}
            strokeDasharray={`${RING_LENGTH * (fraction.done / fraction.total)} ${RING_LENGTH}`} />}
        </svg>}
        <span className="cr-count-fraction" data-complete={complete} data-empty={!fraction} aria-hidden="true">
          {fraction ? <><b>{fraction.done}</b>/{fraction.total}</> : REVIEW_COUNT_NONE}
        </span>
        {facts && <span className="sr-only">, {facts}</span>}
      </span>
    </PopoverPrimitive.Anchor>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content className="cr-portal cr-count-popover" side={side} align="start" sideOffset={8}
        collisionPadding={8} onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={show} onPointerLeave={hide}>
        <ReviewCountDetails label={label} counts={counts} />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}
